/**
 * MandateRecord — The governed transition record.
 *
 * This is the document. Everything that happened, in order, with its signal:
 * the Day 100 outcome and its revision history, the current priorities stack
 * with each "changes when" threshold, the chronological signal log, and the
 * decision record — combined into one printable, read-only account.
 *
 * Distinct from MandateDecisionRecord (/mandate/decisions), which is the
 * editable per-decision CRUD log. This page reads across all four Mandate
 * data types at once; it never writes.
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Printer } from 'lucide-react';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { getDayNumber, getDaysRemaining } from '../../types/mandate';

const SITUATION_LABELS: Record<string, string> = {
  startup: 'Startup — Building something new',
  turnaround: 'Turnaround — Fixing something broken',
  realignment: 'Realignment — Redirecting something that works',
  sustaining: 'Sustaining Success — Growing something strong',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export default function MandateRecord() {
  const {
    transition,
    outcomeVersions,
    priorities,
    reranks,
    decisions,
    currentOutcome,
    fetchAll,
  } = useMandateStore();

  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!transition) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <FileText className="h-12 w-12 text-[var(--text-ter)] mb-4" />
        <p className="text-[var(--text-ter)] text-sm">No active transition.</p>
        <Link to="/apps/mandate/setup" className="mt-3 text-sm text-primary-600 font-medium">
          Start your transition →
        </Link>
      </div>
    );
  }

  const dayNumber = transition.startDate ? getDayNumber(transition.startDate) : null;
  const daysRemaining = transition.endDate ? getDaysRemaining(transition.endDate) : null;
  const activePriorities = priorities.filter((p) => p.status === 'active');
  const inactivePriorities = priorities.filter((p) => p.status !== 'active');

  const sortedVersions = [...outcomeVersions].sort((a, b) => a.version - b.version);
  const originalVersion = sortedVersions[0];

  // Chronological signal log: all reranks sorted by date
  const sortedReranks = [...reranks].sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
  );

  function handlePrint() {
    window.print();
  }

  return (
    <div>
      <PageHeader
        icon={<FileText className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Transition Record"
        subtitle="The governed account of your first 100 days"
        actions={
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium px-3 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
          >
            <Printer className="h-3.5 w-3.5" />
            Print
          </button>
        }
      />

      <div ref={printRef} className="space-y-0">
        {/* ── Document header ── */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8 mb-1">
          <div className="border-b border-[var(--rule)] pb-6 mb-6">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-widest mb-2">
              Transition Record
            </p>
            <h1 className="text-2xl font-bold text-[var(--text-pri)] mb-1">{transition.role}</h1>
            <p className="text-base text-[var(--text-sec)]">{transition.organization}</p>
          </div>

          <div className="grid grid-cols-3 gap-6 text-sm">
            <div>
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-1">
                Situation
              </p>
              <p className="text-[var(--text-sec)]">
                {transition.situationType
                  ? SITUATION_LABELS[transition.situationType] ?? transition.situationType
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-1">
                Started
              </p>
              <p className="text-[var(--text-sec)]">
                {transition.startDate ? formatDate(transition.startDate) : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-1">
                Day 100
              </p>
              <p className="text-[var(--text-sec)]">
                {transition.endDate ? formatDate(transition.endDate) : '—'}
              </p>
            </div>
          </div>

          {dayNumber !== null && (
            <div className="mt-6 flex items-center gap-4">
              <div className="flex-1 h-1.5 rounded-full bg-[var(--hover-tint)]">
                <div
                  className="h-1.5 rounded-full bg-primary-500"
                  style={{ width: `${Math.min((dayNumber / 100) * 100, 100)}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-[var(--text-sec)] flex-shrink-0">
                Day {dayNumber} of 100
                {daysRemaining !== null && daysRemaining > 0 && (
                  <span className="font-normal text-[var(--text-ter)] ml-2">({daysRemaining} remaining)</span>
                )}
              </span>
            </div>
          )}

          {transition.situationNotes && (
            <div className="mt-6 pt-6 border-t border-[var(--rule)]">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
                Situation notes
              </p>
              <p className="text-sm text-[var(--text-sec)] leading-relaxed">{transition.situationNotes}</p>
            </div>
          )}
        </div>

        {/* ── Day 100 Outcome ── */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8 mb-1">
          <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-widest mb-5">
            Day 100 Outcome
          </p>

          {/* Current hypothesis — the one that matters */}
          {currentOutcome && (
            <div className="border-l-4 border-l-indigo-400 pl-5 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                  Current hypothesis
                </span>
                {currentOutcome.version > 1 && (
                  <span className="text-xs text-[var(--text-ter)]">v{currentOutcome.version}</span>
                )}
              </div>
              <p className="text-base text-[var(--text-pri)] leading-relaxed">{currentOutcome.hypothesis}</p>
            </div>
          )}

          {/* Revision history — only show if there were changes */}
          {sortedVersions.length > 1 && (
            <div>
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
                Revision history ({sortedVersions.length - 1} revision
                {sortedVersions.length > 2 ? 's' : ''})
              </p>
              <div className="space-y-4">
                {sortedVersions.map((v, i) => (
                  <div key={v.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          v.isCurrent ? 'bg-[var(--slate)]' : 'bg-[var(--rule-bright)]'
                        }`}
                      />
                      {i < sortedVersions.length - 1 && (
                        <div className="w-px flex-1 bg-[var(--hover-tint)] mt-1" />
                      )}
                    </div>
                    <div className="pb-4 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-[var(--text-ter)]">v{v.version}</span>
                        <span className="text-xs text-[var(--text-ter)]">{formatDate(v.createdAt)}</span>
                        {v.isCurrent && (
                          <span className="text-xs bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] font-medium px-1.5 py-0.5 rounded">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[var(--text-sec)] leading-relaxed mb-1">{v.hypothesis}</p>
                      {v.revisionReason && (
                        <p className="text-xs text-[var(--text-ter)] italic">
                          Revised because: {v.revisionReason}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Original only — show inline if no revisions */}
          {sortedVersions.length === 1 && originalVersion && (
            <p className="text-xs text-[var(--text-ter)] mt-2">
              Set on {formatDate(originalVersion.createdAt)} · No revisions yet.
            </p>
          )}
        </div>

        {/* ── Current Priorities ── */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8 mb-1">
          <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-widest mb-5">
            Priorities Stack
          </p>

          {activePriorities.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)]">No priorities set yet.</p>
          ) : (
            <div className="space-y-5">
              {activePriorities.map((p) => (
                <div key={p.id} className="flex gap-4">
                  <div className="w-7 h-7 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {p.rank}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[var(--text-pri)] mb-0.5">{p.title}</p>
                    {p.rationale && (
                      <p className="text-sm text-[var(--text-sec)] leading-relaxed mb-1">{p.rationale}</p>
                    )}
                    {p.description && (
                      <p className="text-xs text-[var(--text-ter)]">
                        <span className="font-medium text-[var(--text-ter)]">Changes when:</span>{' '}
                        {p.description}
                      </p>
                    )}
                    <p className="text-xs text-[var(--text-ter)] mt-1">Added {formatDateShort(p.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {inactivePriorities.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[var(--rule)]">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
                Completed &amp; Dropped
              </p>
              <div className="space-y-2">
                {inactivePriorities.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 opacity-60">
                    <span className="text-xs font-medium text-[var(--text-ter)] w-16">{p.status}</span>
                    <span className="text-sm text-[var(--text-sec)] line-through">{p.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Signal Log ── */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8 mb-1">
          <div className="flex items-center justify-between mb-5">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-widest">
              Signal Log
            </p>
            <span className="text-xs text-[var(--text-ter)]">
              {sortedReranks.length} event{sortedReranks.length !== 1 ? 's' : ''}
            </span>
          </div>

          {sortedReranks.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)]">
              No re-ranks yet. Every time you re-rank a priority, the signal appears here.
            </p>
          ) : (
            <div className="space-y-4">
              {sortedReranks.map((r) => {
                const p = priorities.find((pr) => pr.id === r.priorityId);
                return (
                  <div key={r.id} className="flex gap-4">
                    <div className="text-xs text-[var(--text-ter)] w-16 flex-shrink-0 pt-0.5 font-medium">
                      {formatDateShort(r.occurredAt)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[var(--text-sec)]">
                        <span className="font-medium">&ldquo;{p?.title ?? 'priority'}&rdquo;</span>{' '}
                        moved{' '}
                        <span className="font-medium">
                          #{r.previousRank ?? '?'} → #{r.newRank ?? '?'}
                        </span>
                      </p>
                      <p className="text-sm text-[var(--text-ter)] mt-0.5 leading-relaxed">{r.signal}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Decision Record ── */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8">
          <div className="flex items-center justify-between mb-5">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-widest">
              Decision Record
            </p>
            <span className="text-xs text-[var(--text-ter)]">
              {decisions.length} decision{decisions.length !== 1 ? 's' : ''}
            </span>
          </div>

          {decisions.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)]">No decisions logged yet.</p>
          ) : (
            <div className="space-y-6">
              {decisions.map((d) => (
                <div key={d.id} className="flex gap-4">
                  <div className="text-xs text-[var(--text-ter)] w-16 flex-shrink-0 pt-0.5 font-medium">
                    {formatDateShort(d.createdAt)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[var(--text-pri)] mb-0.5">{d.title}</p>
                    {d.description && <p className="text-sm text-[var(--text-sec)] mb-1">{d.description}</p>}
                    <p className="text-sm text-[var(--text-ter)] leading-relaxed mb-1">{d.rationale}</p>
                    {d.signal && (
                      <p className="text-xs text-[var(--text-ter)] italic">Signal: {d.signal}</p>
                    )}
                    {d.peopleConsulted && (
                      <p className="text-xs text-[var(--text-ter)] mt-1">Consulted: {d.peopleConsulted}</p>
                    )}
                    {d.outcome && (
                      <p className="text-xs text-[var(--text-ter)] mt-1 font-medium">Outcome: {d.outcome}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer nav */}
      <div className="mt-6 pt-4 border-t border-[var(--rule)] flex items-center justify-between text-sm text-[var(--text-ter)]">
        <Link to="/apps/mandate/dashboard" className="hover:text-[var(--text-sec)]">
          ← Dashboard
        </Link>
        <span>
          Generated Day {dayNumber ?? '—'}
          {transition.endDate && ` · Day 100: ${formatDate(transition.endDate)}`}
        </span>
      </div>
    </div>
  );
}
